/**
 * Semantic tokens define design system meanings
 * These map to primitive tokens and are used by component tokens
 *
 * Architecture:
 * - Primitives: Base values (orange.500, gray.300) - no context
 * - Semantic: Design meanings (primary.color → {orange.500}) ← YOU ARE HERE
 * - Components: Component-specific (button.background → {primary.color})
 *
 * @see https://primeng.org/theming#semantic-tokens
 */
export const semanticTokens = {
  // Transition duration - controls animation speed globally across all PrimeNG components
  // Components reference this via {transition.duration}
  // Default Aura preset uses ~150ms, adjust as needed (e.g., '100ms' for faster, '300ms' for slower)
  transition: {
    duration: '50ms',
  },

  // Control density defaults.
  // html font-size is 14px in both apps, so 6px vertical padding yields a 35px control height.
  formField: {
    paddingY: '6px',
  },

  // Primary color palette - maps to orange primitive
  // This defines the primary brand color scale
  primary: {
    50: '{orange.50}',
    100: '{orange.100}',
    200: '{orange.200}',
    300: '{orange.300}',
    400: '{orange.400}',
    500: '{orange.500}',
    600: '{orange.600}',
    700: '{orange.700}',
    800: '{orange.800}',
    900: '{orange.900}',
    950: '{orange.950}',
  },

  // Color scheme tokens (light/dark mode)
  // This is a special token group per PrimeNG docs for theme-aware tokens
  // @see https://primeng.org/theming#color-scheme
  colorScheme: {
    light: {
      // Primary color semantic tokens
      // These control primary colors used across components (form fields, focus rings, etc.)
      // Note: Button colors are overridden in button component tokens
      primary: {
        color: '{orange.500}',
        contrastColor: '{white}',
        hoverColor: '{orange.600}',
        activeColor: '{orange.700}',
      },

      // Surface color palette - used for backgrounds
      surface: {
        0: '{white}',
        50: '{gray.50}',
        100: '{gray.100}',
        200: '{gray.200}',
        300: '{gray.300}',
        400: '{gray.400}',
        500: '{gray.500}',
        600: '{gray.600}',
        700: '{gray.700}',
        800: '{gray.800}',
        900: '{gray.900}',
        950: '{gray.950}',
      },

      // Text color semantic tokens
      text: {
        color: '{gray.900}',
        secondary: '{gray.600}',
        muted: '{gray.500}',
        disabled: '{gray.400}',
      },

      // Border color semantic tokens
      border: {
        main: '{gray.300}',
        alt: '{gray.400}',
        focus: '{primary.color}',
      },

      // Navigation semantic tokens
      // Used by TieredMenu and other navigation components
      // TieredMenu uses --p-tieredmenu-item-padding which references --p-navigation-item-padding
      // Set to match menu.item.padding (0.75rem) to ensure consistent spacing
      navigation: {
        item: {
          padding: '0.75rem',
        },
      },
    },
    // Add dark mode here when needed
    // dark: { ... }
  },
};
